import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import TopBar from '../components/layout/TopBar';
import MapCanvas from '../components/Map/MapCanvas';
import LotDetailDrawer from '../components/Map/LotDetailDrawer';
import { Icon } from '@iconify/react';
import {
  GlassCard, Button, Badge, InputField, Modal, SectionHeader, Divider, DataTable,
} from '../components/ui';
import IconPicker from '../components/ui/IconPicker';
import { useToast } from '../context/ToastContext';
import type { Lot, LotStatus, Section, LotType, InventoryPolicy, LotAuditLog } from '../types/inventory';
import { STATUS_CONFIG, CATEGORY_CONFIG, fmt } from '../types/inventory';
import { MOCK_SECTIONS, MOCK_LOT_TYPES, MOCK_LOTS, MOCK_AUDIT_LOGS, MOCK_POLICIES } from '../data/inventoryMockData';
import { api } from '../lib/api';

type ViewMode = 'map' | 'list' | 'designer' | 'policies';

const Inventory: React.FC = () => {
  const { addToast } = useToast();
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [lots, setLots] = useState<Lot[]>(MOCK_LOTS);
  const [sections, setSections] = useState<Section[]>(MOCK_SECTIONS);
  const [lotTypes, setLotTypes] = useState<LotType[]>(MOCK_LOT_TYPES);
  const [auditLogs, setAuditLogs] = useState<LotAuditLog[]>(MOCK_AUDIT_LOGS);
  const [policies, setPolicies] = useState<InventoryPolicy[]>(MOCK_POLICIES);
  const [showFreeformModal, setShowFreeformModal] = useState(false);
  const [freeformSection, setFreeformSection] = useState<Section | null>(null);
  const [freeformForm, setFreeformForm] = useState<any>({});
  const [placementMode, setPlacementMode] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // ─── Fetch from API ────────────────────────────────────
  const fetchInventory = useCallback(async () => {
    try {
      const [secData, ltData, lotData] = await Promise.all([
        api.inventory.sections.list(),
        api.inventory.lotTypes.list(),
        api.inventory.lots.list(),
      ]);
      if (secData?.length) setSections(secData.map((s: any) => ({ ...s, layoutMode: s.layoutMode || 'GRID' })));
      if (ltData?.length) setLotTypes(ltData);
      if (lotData?.length) setLots(lotData.map((l: any) => ({ ...l, isFreeform: l.isFreeform ?? false })));
    } catch {
      // keep mock data as fallback
    }
  }, []);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  const [filterStatus, setFilterStatus] = useState<LotStatus | ''>('');
  const [filterSection, setFilterSection] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const drawerRef = useRef<HTMLDivElement>(null);

  // ESC key to close drawer
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedLot) setSelectedLot(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedLot]);

  // Click outside drawer to close
  useEffect(() => {
    if (!selectedLot || viewMode !== 'map') return;
    const handleClick = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        setSelectedLot(null);
      }
    };
    // Delay to avoid closing on the same click that opened it
    const timer = setTimeout(() => window.addEventListener('mousedown', handleClick), 50);
    return () => { clearTimeout(timer); window.removeEventListener('mousedown', handleClick); };
  }, [selectedLot, viewMode]);

  // Designer state
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionForm, setSectionForm] = useState<any>({});

  // Lot Type CRUD state
  const [editingLotType, setEditingLotType] = useState<LotType | null>(null);
  const [showLotTypeModal, setShowLotTypeModal] = useState(false);
  const [lotTypeForm, setLotTypeForm] = useState<any>({});

  // ─── Computed stats ───────────────────────────────────
  const stats = useMemo((): Record<string, number> => {
    const result: Record<string, number> = {};
    Object.keys(STATUS_CONFIG).forEach(s => { result[s] = lots.filter(l => l.status === s).length; });
    result.total = lots.length;
    result.totalValue = lots.reduce((s, l) => s + l.price, 0);
    result.soldValue = lots.filter(l => l.status === 'SOLD').reduce((s, l) => s + l.price, 0);
    return result;
  }, [lots]);

  // ─── Filtered lots for list view ──────────────────────
  const filteredLots = useMemo(() => {
    return lots.filter(l => {
      if (filterStatus && l.status !== filterStatus) return false;
      if (filterSection && l.sectionId !== filterSection) return false;
      if (searchQuery && !l.lotNumber.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [lots, filterStatus, filterSection, searchQuery]);

  // ─── Handlers ─────────────────────────────────────────
  const addAuditLog = (lotId: string, action: string, from?: string, to?: string, reason?: string) => {
    setAuditLogs(prev => [
      { id: Date.now().toString(), lotId, action, fromValue: from, toValue: to, performedBy: 'Current User', reason, createdAt: new Date().toISOString() },
      ...prev,
    ]);
  };

  const handleStatusChange = async (lotId: string, newStatus: LotStatus, reason?: string) => {
    try {
      await api.inventory.lots.update(lotId, { status: newStatus, _reason: reason, _performedBy: 'Current User' });
    } catch { /* continue with local update */ }
    setLots(prev => prev.map(l => {
      if (l.id !== lotId) return l;
      addAuditLog(lotId, 'STATUS_CHANGE', l.status, newStatus, reason);
      return { ...l, status: newStatus, isLocked: newStatus === 'LOCKED' };
    }));
    setSelectedLot(prev => prev?.id === lotId ? { ...prev, status: newStatus, isLocked: newStatus === 'LOCKED' } : prev);
    addToast('success', 'Status Updated', `Lot moved to ${STATUS_CONFIG[newStatus].label}`);
  };

  const handlePriceChange = async (lotId: string, newPrice: number) => {
    const lot = lots.find(l => l.id === lotId);
    try {
      await api.inventory.lots.update(lotId, { price: newPrice });
    } catch { /* continue */ }
    setLots(prev => prev.map(l => l.id === lotId ? { ...l, price: newPrice } : l));
    setSelectedLot(prev => prev?.id === lotId ? { ...prev, price: newPrice } : prev);
    addAuditLog(lotId, 'PRICE_CHANGE', String(lot?.price), String(newPrice));
    addToast('success', 'Price Updated', `New price: ${fmt(newPrice)}`);
  };

  const handleLockToggle = async (lotId: string, lock: boolean, reason?: string) => {
    try {
      await api.inventory.lots.update(lotId, { isLocked: lock, lockedReason: lock ? reason : null });
    } catch { /* continue */ }
    setLots(prev => prev.map(l => l.id !== lotId ? l : { ...l, isLocked: lock, lockedReason: lock ? reason : undefined }));
    setSelectedLot(prev => prev?.id === lotId ? { ...prev, isLocked: lock, lockedReason: lock ? reason : undefined } : prev);
    addAuditLog(lotId, lock ? 'LOCK' : 'UNLOCK', undefined, lock ? 'LOCKED' : 'UNLOCKED', reason);
    addToast(lock ? 'warning' : 'info', lock ? 'Lot Locked' : 'Lot Unlocked');
  };

  const handleAddNote = (lotId: string, note: string) => {
    setLots(prev => prev.map(l => l.id !== lotId ? l : { ...l, notes: l.notes ? `${l.notes}\n${note}` : note }));
    setSelectedLot(prev => prev?.id === lotId ? { ...prev, notes: prev.notes ? `${prev.notes}\n${note}` : note } : prev);
    addAuditLog(lotId, 'NOTE', undefined, note);
    addToast('info', 'Note Added');
  };

  const handleRepositionLot = async (lotId: string, data: { x_coord: number; y_coord: number; width?: number; height?: number }) => {
    try {
      await api.inventory.lots.update(lotId, data);
      setLots(prev => prev.map(l => l.id !== lotId ? l : { ...l, ...data }));
      setSelectedLot(prev => prev?.id === lotId ? { ...prev, ...data } : prev);
      addToast('success', 'Position Updated', 'Lot has been repositioned.');
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to reposition lot');
    }
  };

  const handleLotDragEnd = async (lotId: string, newX: number, newY: number) => {
    try {
      await api.inventory.lots.update(lotId, { x_coord: newX, y_coord: newY });
      setLots(prev => prev.map(l => l.id !== lotId ? l : { ...l, x_coord: newX, y_coord: newY }));
      setSelectedLot(prev => prev?.id === lotId ? { ...prev, x_coord: newX, y_coord: newY } : prev);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save position');
      fetchInventory();
    }
  };

  const handleSectionDragEnd = async (sectionId: string, newX: number, newY: number) => {
    try {
      await api.inventory.sections.update(sectionId, { x: newX, y: newY });
      setSections(prev => prev.map(s => s.id !== sectionId ? s : { ...s, x: newX, y: newY }));
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save section position');
      fetchInventory();
    }
  };

  const handleLotResize = async (lotId: string, newX: number, newY: number, newW: number, newH: number) => {
    try {
      await api.inventory.lots.update(lotId, { x_coord: newX, y_coord: newY, width: newW, height: newH });
      setLots(prev => prev.map(l => l.id !== lotId ? l : { ...l, x_coord: newX, y_coord: newY, width: newW, height: newH }));
      setSelectedLot(prev => prev?.id === lotId ? { ...prev, x_coord: newX, y_coord: newY, width: newW, height: newH } : prev);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save resize');
      fetchInventory();
    }
  };

  const handleAutoFitSections = async () => {
    const updated: Section[] = [...sections];
    for (const sec of sections) {
      if (sec.layoutMode !== 'FREEFORM' && sec.layoutMode !== 'HYBRID') continue;
      const secLots = lots.filter(l => l.sectionId === sec.id);
      if (secLots.length === 0) continue;
      const PAD = 24, TOP_PAD = 40;
      const minX = Math.min(...secLots.map(l => l.x_coord));
      const minY = Math.min(...secLots.map(l => l.y_coord));
      const maxX = Math.max(...secLots.map(l => l.x_coord + (l.width || sec.lotWidth || 50)));
      const maxY = Math.max(...secLots.map(l => l.y_coord + (l.height || sec.lotHeight || 50)));
      const newX = Math.round(minX - PAD);
      const newY = Math.round(minY - TOP_PAD);
      const newW = Math.round(maxX - minX + PAD * 2);
      const newH = Math.round(maxY - minY + PAD + TOP_PAD);
      if (Math.abs(sec.x - newX) < 3 && Math.abs(sec.y - newY) < 3 &&
          Math.abs(sec.width - newW) < 3 && Math.abs(sec.height - newH) < 3) continue;
      try {
        await api.inventory.sections.update(sec.id, { x: newX, y: newY, width: newW, height: newH });
        const idx = updated.findIndex(s => s.id === sec.id);
        if (idx >= 0) updated[idx] = { ...updated[idx], x: newX, y: newY, width: newW, height: newH };
      } catch { /* silent */ }
    }
    setSections(updated);
    addToast('success', 'Layout Saved', 'Section bounds auto-fitted to lot positions.');
  };

  const handleEditDone = async () => {
    setEditMode(false);
    await handleAutoFitSections();
  };

  const handleDeleteLot = async (lotId: string) => {
    try {
      await api.inventory.lots.delete(lotId);
      setLots(prev => prev.filter(l => l.id !== lotId));
      setSelectedLot(null);
      addToast('info', 'Lot Deleted', 'The lot has been removed.');
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to delete lot');
    }
  };

  const handlePolicyChange = (policyId: string, newValue: string) => {
    setPolicies(prev => prev.map(p => p.id === policyId ? { ...p, value: newValue } : p));
    addToast('success', 'Policy Updated');
  };

  // Delete section
  const handleDeleteSection = async (sec: Section) => {
    const secLots = lots.filter(l => l.sectionId === sec.id);
    if (secLots.length > 0) {
      // Delete all lots in the section first
      const hasSales = secLots.some(l => l.status === 'SOLD' || l.status === 'RESERVED');
      if (hasSales) {
        addToast('error', 'Cannot Delete', `${sec.label} has sold or reserved lots.`);
        return;
      }
      try {
        for (const lot of secLots) {
          await api.inventory.lots.delete(lot.id);
        }
      } catch (err: any) {
        addToast('error', 'Error', err.message || 'Failed to delete lots');
        return;
      }
    }
    try {
      await api.inventory.sections.delete(sec.id);
      setSections(prev => prev.filter(s => s.id !== sec.id));
      setLots(prev => prev.filter(l => l.sectionId !== sec.id));
      addToast('info', 'Section Deleted', `${sec.label} has been removed.`);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to delete section');
    }
  };

  // Section designer
  const openSectionEditor = (sec?: Section) => {
    if (sec) {
      setEditingSection(sec);
      setSectionForm({ ...sec });
    } else {
      setEditingSection(null);
      setSectionForm({
        name: '', label: '', description: '', x: 100, y: 100, width: 400, height: 300,
        layoutMode: 'GRID', rows: 4, cols: 6, lotWidth: 48, lotHeight: 48, lotGap: 6,
        phase: '', category: 'LAWN', color: '#22C55E',
      });
    }
    setShowSectionModal(true);
  };

  const handleSaveSection = async () => {
    if (!sectionForm.name || !sectionForm.label) {
      addToast('error', 'Validation', 'Name and label are required.');
      return;
    }
    try {
      if (editingSection) {
        const { id, _count, ...updateData } = sectionForm;
        const updated = await api.inventory.sections.update(editingSection.id, updateData);
        setSections(prev => prev.map(s => s.id === editingSection.id ? { ...s, ...updated, layoutMode: updated.layoutMode || 'GRID' } : s));
        addToast('success', 'Section Updated', `${sectionForm.label} has been updated.`);
      } else {
        const mode = sectionForm.layoutMode || 'GRID';
        // Auto-calculate section dimensions from grid config for consistent margins
        const PAD_X = 24, PAD_TOP = 40, PAD_BOTTOM = 16;
        const lw = sectionForm.lotWidth || 48, lh = sectionForm.lotHeight || 48, gap = sectionForm.lotGap || 6;
        const rows = sectionForm.rows || 4, cols = sectionForm.cols || 6;
        const calcWidth = mode !== 'FREEFORM' ? PAD_X * 2 + cols * lw + Math.max(0, cols - 1) * gap : (sectionForm.width || 400);
        const calcHeight = mode !== 'FREEFORM' ? PAD_TOP + PAD_BOTTOM + rows * lh + Math.max(0, rows - 1) * gap : (sectionForm.height || 300);
        const created = await api.inventory.sections.create({
          ...sectionForm, rotation: 0, isActive: true, sortOrder: sections.length + 1,
          layoutMode: mode, width: calcWidth, height: calcHeight,
        });
        setSections(prev => [...prev, { ...created, layoutMode: created.layoutMode || 'GRID', width: calcWidth, height: calcHeight }]);

        // Auto-generate grid lots for GRID and HYBRID sections
        if (mode !== 'FREEFORM' && created.rows > 0 && created.cols > 0) {
          const defaultType = lotTypes.find(t => t.isActive);
          const gridLots = [];
          for (let r = 0; r < created.rows; r++) {
            for (let c = 0; c < created.cols; c++) {
              gridLots.push({
                lotNumber: `${created.name}${r * created.cols + c + 1}`,
                price: defaultType?.basePrice || 50000,
                lotTypeId: defaultType?.id,
                row: r, col: c,
                x_coord: created.x + PAD_X + c * (lw + gap),
                y_coord: created.y + PAD_TOP + r * (lh + gap),
                isFreeform: false,
              });
            }
          }
          try {
            await api.inventory.lots.batchCreate({ sectionId: created.id, lots: gridLots });
            const freshLots = await api.inventory.lots.list({ sectionId: created.id });
            setLots(prev => [...prev, ...freshLots.map((l: any) => ({ ...l, isFreeform: l.isFreeform ?? false }))]);
            addToast('success', 'Section Created', `${sectionForm.label} with ${gridLots.length} grid lots.`);
          } catch {
            addToast('success', 'Section Created', `${sectionForm.label} created (lots generation failed — add manually).`);
          }
        } else {
          addToast('success', 'Section Created', `${sectionForm.label} has been added. Use "Add Plot" to place individual lots.`);
        }
      }
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save section');
    }
    setShowSectionModal(false);
  };

  // ─── Lot Type CRUD ────────────────────────────────────
  const openLotTypeEditor = (lt?: LotType) => {
    if (lt) {
      setEditingLotType(lt);
      setLotTypeForm({ ...lt });
    } else {
      setEditingLotType(null);
      setLotTypeForm({
        name: '', code: '', category: 'LAWN', description: '',
        defaultWidth: 48, defaultHeight: 48, capacity: 1,
        basePrice: 50000, color: '#0D9488', icon: '', imageUrl: '', isActive: true,
      });
    }
    setShowLotTypeModal(true);
  };

  const handleSaveLotType = async () => {
    if (!lotTypeForm.name || !lotTypeForm.code) {
      addToast('error', 'Validation', 'Name and code are required.');
      return;
    }
    try {
      if (editingLotType) {
        const { id, ...updateData } = lotTypeForm;
        const updated = await api.inventory.lotTypes.update(editingLotType.id, updateData);
        setLotTypes(prev => prev.map(t => t.id === editingLotType.id ? updated : t));
        setLots(prev => prev.map(lot => lot.lotTypeId === editingLotType.id ? { ...lot, lotType: updated } : lot));
        addToast('success', 'Lot Type Updated', `${lotTypeForm.name} has been updated.`);
      } else {
        const created = await api.inventory.lotTypes.create({ ...lotTypeForm, sortOrder: lotTypes.length + 1 });
        setLotTypes(prev => [...prev, created]);
        addToast('success', 'Lot Type Created', `${lotTypeForm.name} has been added.`);
      }
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save lot type');
    }
    setShowLotTypeModal(false);
  };

  const handleDeleteLotType = async (lt: LotType) => {
    const inUse = lots.some(l => l.lotTypeId === lt.id);
    if (inUse) {
      addToast('error', 'Cannot Delete', `${lt.name} is in use by existing lots.`);
      return;
    }
    try {
      await api.inventory.lotTypes.delete(lt.id);
    } catch { /* continue */ }
    setLotTypes(prev => prev.filter(t => t.id !== lt.id));
    addToast('info', 'Lot Type Deleted', `${lt.name} has been removed.`);
  };

  const handleToggleLotType = async (lt: LotType) => {
    try {
      await api.inventory.lotTypes.update(lt.id, { isActive: !lt.isActive });
    } catch { /* continue */ }
    setLotTypes(prev => prev.map(t => t.id === lt.id ? { ...t, isActive: !t.isActive } : t));
    addToast('info', lt.isActive ? 'Lot Type Disabled' : 'Lot Type Enabled');
  };

  // ─── Freeform Lot Handlers ─────────────────────────────
  const openFreeformModal = (sec: Section) => {
    setFreeformSection(sec);
    setFreeformForm({
      lotNumber: `${sec.name}${lots.filter(l => l.sectionId === sec.id).length + 1}`,
      lotTypeId: lotTypes[0]?.id || '',
      price: lotTypes[0]?.basePrice || 50000,
      x_coord: 50,
      y_coord: 50,
      width: sec.lotWidth || 60,
      height: sec.lotHeight || 60,
    });
    setShowFreeformModal(true);
  };

  const handleSaveFreeformLot = async () => {
    if (!freeformSection || !freeformForm.lotNumber) {
      addToast('error', 'Validation', 'Lot number is required.');
      return;
    }
    try {
      const created = await api.inventory.lots.create({
        sectionId: freeformSection.id,
        lotTypeId: freeformForm.lotTypeId || undefined,
        lotNumber: freeformForm.lotNumber,
        price: Number(freeformForm.price),
        status: 'AVAILABLE',
        row: 0, col: 0,
        isFreeform: true,
        x_coord: freeformSection.x + Number(freeformForm.x_coord),
        y_coord: freeformSection.y + Number(freeformForm.y_coord),
        width: Number(freeformForm.width),
        height: Number(freeformForm.height),
      });
      setLots(prev => [...prev, { ...created, isFreeform: true }]);
      addToast('success', 'Lot Created', `Freeform lot ${freeformForm.lotNumber} added to ${freeformSection.label}`);
      setShowFreeformModal(false);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to create lot');
    }
  };

  // Click-to-place on canvas
  const handlePlaceLot = (sectionId: string, absX: number, absY: number) => {
    const sec = sections.find(s => s.id === sectionId);
    if (!sec) return;
    // Open freeform modal pre-filled with the click coordinates (relative to section)
    const relX = Math.max(0, absX - sec.x);
    const relY = Math.max(0, absY - sec.y);
    setFreeformSection(sec);
    setFreeformForm({
      lotNumber: `${sec.name}${lots.filter(l => l.sectionId === sec.id).length + 1}`,
      lotTypeId: lotTypes[0]?.id || '',
      price: lotTypes[0]?.basePrice || 50000,
      x_coord: Math.round(relX),
      y_coord: Math.round(relY),
      width: sec.lotWidth || 60,
      height: sec.lotHeight || 60,
    });
    setShowFreeformModal(true);
    setPlacementMode(false);
  };

  // ─── View mode tabs config ────────────────────────────
  const viewTabs: { key: ViewMode; label: string; icon: string }[] = [
    { key: 'map', label: 'Map', icon: 'solar:map-bold-duotone' },
    { key: 'list', label: 'List', icon: 'solar:list-bold-duotone' },
    { key: 'designer', label: 'Designer', icon: 'solar:ruler-pen-bold-duotone' },
    { key: 'policies', label: 'Policies', icon: 'solar:shield-bold-duotone' },
  ];

  // ─── Status badge variant ────────────────────────────
  const statusVariant = (s: string) => {
    switch (s) {
      case 'AVAILABLE': return 'available';
      case 'RESERVED': return 'reserved';
      case 'SOLD': return 'sold';
      default: return 'default';
    }
  };

  // ─── List columns ─────────────────────────────────────
  const listColumns = [
    {
      key: 'lotNumber', header: 'Lot',
      render: (row: Lot) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: STATUS_CONFIG[row.status]?.color || '#64748B',
          }} />
          <span style={{ color: 'var(--dark-100)', fontWeight: 600 }}>{row.lotNumber}</span>
          {row.isFreeform && <Badge variant="reserved" style={{ fontSize: '9px', padding: '1px 5px' }}>FREE</Badge>}
        </div>
      ),
    },
    {
      key: 'section', header: 'Section',
      render: (row: Lot) => {
        const sec = sections.find(s => s.id === row.sectionId);
        return <span style={{ color: 'var(--dark-300)', fontSize: '13px' }}>{sec?.label || row.sectionId}</span>;
      },
    },
    {
      key: 'type', header: 'Type',
      render: (row: Lot) => <span style={{ color: 'var(--dark-400)', fontSize: '12px' }}>{row.lotType?.name || '—'}</span>,
    },
    {
      key: 'price', header: 'Price',
      render: (row: Lot) => <span style={{ color: 'var(--dark-200)', fontWeight: 600 }}>{fmt(row.price)}</span>,
    },
    {
      key: 'status', header: 'Status',
      render: (row: Lot) => <Badge variant={statusVariant(row.status) as any}>{row.status}</Badge>,
    },
    {
      key: 'occupant', header: 'Occupant',
      render: (row: Lot) => <span style={{ color: 'var(--dark-400)', fontSize: '12px' }}>{row.occupantName || '—'}</span>,
    },
  ];

  // ─── RENDER: Status Bar ───────────────────────────────
  const renderStatusBar = () => (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {(Object.entries(STATUS_CONFIG) as [LotStatus, typeof STATUS_CONFIG[LotStatus]][]).map(([key, cfg]) => {
        const count = stats[key] || 0;
        const isActive = filterStatus === key;
        return (
          <button key={key} onClick={() => setFilterStatus(isActive ? '' : key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              background: isActive ? cfg.bg : 'var(--bg-elevated)',
              fontFamily: 'var(--font-body)', transition: 'all 0.15s ease',
            }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: cfg.color }} />
            <span style={{ fontSize: '11px', fontWeight: 600, color: isActive ? cfg.color : 'var(--text-secondary)' }}>
              {cfg.label}
            </span>
            <span style={{
              fontSize: '10px', fontWeight: 700, color: isActive ? cfg.color : 'var(--text-muted)',
              background: isActive ? `${cfg.color}20` : 'var(--bg-elevated)',
              borderRadius: '10px', padding: '1px 6px',
            }}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );

  // ─── RENDER: Map View ─────────────────────────────────
  const hasFreeformSections = sections.some(s => s.layoutMode === 'FREEFORM' || s.layoutMode === 'HYBRID');

  const renderMapView = () => (
    <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
      {/* Edit mode banner */}
      {editMode && !placementMode && (
        <div style={{
          position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 20,
          background: 'rgba(99,102,241,0.90)', backdropFilter: 'blur(8px)',
          borderRadius: '8px', padding: '8px 16px',
          display: 'flex', alignItems: 'center', gap: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          <Icon icon="solar:move-bold-duotone" width={18} style={{ color: '#fff' }} />
          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Edit Layout — Drag to move, click to resize handles. Bounds auto-fit on Done.</span>
          <button onClick={handleEditDone} style={{
            background: 'rgba(255,255,255,0.20)', border: 'none', borderRadius: '4px',
            padding: '3px 10px', color: '#fff', fontSize: '11px', fontWeight: 600,
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>Done</button>
        </div>
      )}
      {/* Placement mode banner */}
      {placementMode && (
        <div style={{
          position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 20,
          background: 'rgba(13,148,136,0.90)', backdropFilter: 'blur(8px)',
          borderRadius: '8px', padding: '8px 16px',
          display: 'flex', alignItems: 'center', gap: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          <Icon icon="solar:map-point-add-bold-duotone" width={18} style={{ color: '#fff' }} />
          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Click on a freeform/hybrid section to place a lot</span>
          <button onClick={() => setPlacementMode(false)} style={{
            background: 'rgba(255,255,255,0.20)', border: 'none', borderRadius: '4px',
            padding: '3px 10px', color: '#fff', fontSize: '11px', fontWeight: 600,
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>Cancel</button>
        </div>
      )}
      <div style={{
        width: '100%', height: '100%', borderRadius: 'var(--radius-lg)', overflow: 'hidden',
        border: editMode ? '2px solid rgba(99,102,241,0.60)' : placementMode ? '2px solid var(--primary-600)' : '1px solid var(--glass-border)',
        cursor: placementMode ? 'crosshair' : undefined,
      }}>
        <MapCanvas
          sections={sections} lots={lots}
          selectedLotId={selectedLot?.id}
          onLotClick={setSelectedLot}
          onSectionDragEnd={handleSectionDragEnd}
          filterStatus={filterStatus}
          filterSection={filterSection}
          placementMode={placementMode}
          onPlaceLot={handlePlaceLot}
          editMode={editMode}
          onLotDragEnd={handleLotDragEnd}
          onLotResize={handleLotResize}
        />
      </div>
      {selectedLot && (
        <div ref={drawerRef} style={{
          position: 'absolute', top: 0, right: 0, height: '100%', zIndex: 10,
          borderRadius: '0 var(--radius-lg) var(--radius-lg) 0', overflow: 'hidden',
        }}>
          <LotDetailDrawer
            lot={selectedLot}
            section={sections.find(s => s.id === selectedLot.sectionId)}
            auditLogs={auditLogs}
            onClose={() => setSelectedLot(null)}
            onStatusChange={handleStatusChange}
            onPriceChange={handlePriceChange}
            onLockToggle={handleLockToggle}
            onAddNote={handleAddNote}
            onDelete={handleDeleteLot}
            onReposition={handleRepositionLot}
            onTransactionComplete={() => {
              fetchInventory();
              setSelectedLot(null);
            }}
          />
        </div>
      )}
    </div>
  );

  // ─── RENDER: List View ────────────────────────────────
  const renderListView = () => (
    <DataTable columns={listColumns} data={filteredLots} onRowClick={(lot: Lot) => setSelectedLot(lot)} />
  );

  // ─── RENDER: Section Designer ─────────────────────────
  const renderDesigner = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SectionHeader title="Park Sections" icon="solar:ruler-pen-bold-duotone" />
        <Button variant="primary" icon="solar:add-circle-bold-duotone" onClick={() => openSectionEditor()}>
          Add Section
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
        {sections.map(sec => {
          const secLots = lots.filter(l => l.sectionId === sec.id);
          const avail = secLots.filter(l => l.status === 'AVAILABLE').length;
          const reserved = secLots.filter(l => l.status === 'RESERVED').length;
          const sold = secLots.filter(l => l.status === 'SOLD').length;
          const catCfg = CATEGORY_CONFIG[sec.category];
          return (
            <GlassCard key={sec.id} style={{ borderTop: `3px solid ${sec.color || 'var(--primary-600)'}`, position: 'relative' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '6px',
                      background: `${sec.color || 'var(--primary-600)'}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon icon={catCfg?.icon || 'solar:map-bold-duotone'} width={14}
                        style={{ color: sec.color || 'var(--primary-400)' }} />
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>
                      {sec.name} — {sec.label}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginLeft: '36px' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                      {catCfg?.label || sec.category}
                    </span>
                    <span style={{ color: 'var(--text-faint)', fontSize: '10px' }}>·</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                      {sec.phase || 'Phase 1'}
                    </span>
                    {sec.layoutMode === 'GRID' && (
                      <>
                        <span style={{ color: 'var(--text-faint)', fontSize: '10px' }}>·</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                          {sec.rows}×{sec.cols} grid
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                  <button
                    onClick={() => openSectionEditor(sec)}
                    title="Edit Section"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                      transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                    <Icon icon="solar:pen-bold-duotone" width={14} />
                  </button>
                  <button
                    onClick={() => { if (window.confirm(`Delete section "${sec.label}" and all its lots?`)) handleDeleteSection(sec); }}
                    title="Delete Section"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                      borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                      transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                    <Icon icon="solar:trash-bin-trash-bold-duotone" width={14} />
                  </button>
                </div>
              </div>

              {/* Mini preview */}
              {sec.layoutMode === 'FREEFORM' ? (
                <div style={{
                  position: 'relative', height: '100px', borderRadius: '6px',
                  background: 'var(--bg-elevated)', marginBottom: '10px', overflow: 'hidden',
                }}>
                  {secLots.map(lot => {
                    const relX = ((lot.x_coord - sec.x) / sec.width) * 100;
                    const relY = ((lot.y_coord - sec.y) / sec.height) * 100;
                    const relW = ((lot.width || sec.lotWidth || 50) / sec.width) * 100;
                    const relH = ((lot.height || sec.lotHeight || 50) / sec.height) * 100;
                    return (
                      <div key={lot.id} style={{
                        position: 'absolute',
                        left: `${relX}%`, top: `${relY}%`,
                        width: `${Math.max(relW, 4)}%`, height: `${Math.max(relH, 8)}%`,
                        borderRadius: '2px',
                        background: STATUS_CONFIG[lot.status]?.color || '#64748B',
                        opacity: 0.7,
                      }} />
                    );
                  })}
                  {secLots.length === 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--dark-600)', fontSize: '11px' }}>
                      No lots placed yet
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${sec.cols || 1}, 1fr)`,
                  gridTemplateRows: `repeat(${sec.rows || 1}, 1fr)`,
                  gap: '3px', 
                  padding: '10px', 
                  borderRadius: '6px',
                  background: 'var(--bg-elevated)', 
                  marginBottom: '10px',
                  aspectRatio: `${sec.cols || 1} / ${sec.rows || 1}`,
                }}>
                  {Array.from({ length: sec.rows * sec.cols }).map((_, idx) => {
                    const lot = secLots.filter(l => !l.isFreeform)[idx];
                    return (
                      <div key={idx} style={{
                        borderRadius: '2px',
                        background: lot ? (STATUS_CONFIG[lot.status]?.color || '#64748B') : 'rgba(148,163,184,0.1)',
                        opacity: lot ? 0.7 : 0.3,
                      }} />
                    );
                  })}
                </div>
              )}
              {/* Freeform lots indicator for HYBRID */}
              {sec.layoutMode === 'HYBRID' && secLots.filter(l => l.isFreeform).length > 0 && (
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '8px', fontStyle: 'italic' }}>
                  + {secLots.filter(l => l.isFreeform).length} freeform plot{secLots.filter(l => l.isFreeform).length > 1 ? 's' : ''}
                </div>
              )}

              {/* Stats + Actions */}
              <div style={{ 
                display: 'flex', 
                gap: '6px', 
                alignItems: 'center',
                paddingTop: '10px',
                borderTop: '1px solid var(--border-base)',
                flexWrap: 'wrap',
              }}>
                <Badge variant="available">{avail} avail</Badge>
                <Badge variant="reserved">{reserved} res</Badge>
                <Badge variant="sold">{sold} sold</Badge>
                <div style={{ flex: 1, minWidth: '40px' }} />
                {(sec.layoutMode === 'FREEFORM' || sec.layoutMode === 'HYBRID') && (
                  <button
                    onClick={() => openFreeformModal(sec)}
                    title="Add Plot"
                    style={{
                      background: 'none', border: '1px solid var(--border-base)', cursor: 'pointer',
                      padding: '4px 10px', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                      fontSize: '11px', fontWeight: 500, fontFamily: 'var(--font-body)',
                      transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'var(--nav-item-active-bg)';
                      e.currentTarget.style.color = 'var(--nav-item-active-color)';
                      e.currentTarget.style.borderColor = 'var(--nav-item-active-color)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'none';
                      e.currentTarget.style.color = 'var(--text-muted)';
                      e.currentTarget.style.borderColor = 'var(--border-base)';
                    }}>
                    <Icon icon="solar:add-circle-bold-duotone" width={12} />
                    Add Plot
                  </button>
                )}
                <span style={{ color: 'var(--text-faint)', fontSize: '11px', fontWeight: 600 }}>
                  {secLots.length} lot{secLots.length !== 1 ? 's' : ''}
                </span>
              </div>
            </GlassCard>
          );
        })}
      </div>

      {/* Lot Types */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionHeader title="Lot Types" icon="solar:layers-bold-duotone" />
          <Button variant="primary" size="sm" icon="solar:add-circle-bold-duotone" onClick={() => openLotTypeEditor()}>Add Type</Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px', marginTop: '8px' }}>
          {lotTypes.map(lt => (
            <div key={lt.id} style={{
              padding: '12px 14px', borderRadius: 'var(--radius-md)',
              background: 'var(--glass-bg)', border: '1px solid var(--glass-border)',
              borderLeft: `3px solid ${lt.color}`,
              opacity: lt.isActive ? 1 : 0.5,
              transition: 'opacity 0.15s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Icon icon={lt.icon || 'solar:map-point-bold-duotone'} width={16} style={{ color: lt.color }} />
                <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{lt.name}</span>
                {!lt.isActive && <Badge variant="default">Disabled</Badge>}
                <span style={{ color: 'var(--text-faint)', fontSize: '10px', marginLeft: 'auto' }}>{lt.code}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                <span style={{ color: 'var(--text-muted)' }}>{CATEGORY_CONFIG[lt.category]?.label || lt.category}</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{fmt(lt.basePrice)}</span>
              </div>
              <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '2px' }}>
                {lt.defaultWidth}×{lt.defaultHeight}px · Capacity: {lt.capacity}
              </div>
              {lt.description && <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '4px', fontStyle: 'italic' }}>{lt.description}</div>}
              <div style={{ display: 'flex', gap: '4px', marginTop: '8px', borderTop: '1px solid rgba(148,163,184,0.06)', paddingTop: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => openLotTypeEditor(lt)}
                  title="Edit"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                    borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                    transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  <Icon icon="solar:pen-bold-duotone" width={16} />
                </button>
                <button
                  onClick={() => handleToggleLotType(lt)}
                  title={lt.isActive ? 'Disable' : 'Enable'}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                    borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                    transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  <Icon icon={lt.isActive ? 'solar:eye-closed-bold-duotone' : 'solar:eye-bold-duotone'} width={16} />
                </button>
                <button
                  onClick={() => handleDeleteLotType(lt)}
                  title="Delete"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                    borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                    transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  <Icon icon="solar:trash-bin-trash-bold-duotone" width={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ─── RENDER: Policies ─────────────────────────────────
  const policyCategories = ['reservation', 'pricing', 'transfer', 'approval', 'status', 'maintenance'];
  const policyCategoryConfig: Record<string, { label: string; icon: string; description: string }> = {
    reservation: { label: 'Reservation Rules', icon: 'solar:clock-circle-bold-duotone', description: 'Control how lots are reserved and when reservations expire' },
    pricing: { label: 'Pricing Policies', icon: 'solar:tag-price-bold-duotone', description: 'Manage discounts, seasonal pricing, and markups' },
    transfer: { label: 'Transfer & Upgrade', icon: 'solar:transfer-horizontal-bold-duotone', description: 'Control lot ownership transfers and type upgrades' },
    approval: { label: 'Approval Workflows', icon: 'solar:shield-check-bold-duotone', description: 'Require manager approval for sensitive operations' },
    status: { label: 'Status Workflow', icon: 'solar:routing-bold-duotone', description: 'Define allowed status transitions and queuing' },
    maintenance: { label: 'Maintenance & Limits', icon: 'solar:settings-bold-duotone', description: 'Maintenance holds, blackout dates, agent limits' },
  };

  const renderPolicies = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <SectionHeader title="Inventory Policies" icon="solar:shield-bold-duotone" />
      <div style={{ color: 'var(--dark-400)', fontSize: '13px', marginTop: '-12px' }}>
        Configure rules and workflows that govern how the inventory system behaves. All policies are applied system-wide.
      </div>

      {policyCategories.map(cat => {
        const catPolicies = policies.filter(p => p.category === cat);
        const cfg = policyCategoryConfig[cat];
        if (catPolicies.length === 0) return null;
        return (
          <GlassCard key={cat}>
            <SectionHeader title={cfg.label} icon={cfg.icon} />
            <div style={{ color: 'var(--dark-500)', fontSize: '12px', marginBottom: '12px', marginTop: '-4px' }}>{cfg.description}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {catPolicies.map(policy => (
                <div key={policy.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', borderRadius: '6px', background: 'var(--bg-elevated)',
                }}>
                  <div style={{ flex: 1, marginRight: '12px' }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 500 }}>{policy.label}</div>
                    {policy.description && <div style={{ color: 'var(--dark-500)', fontSize: '11px', marginTop: '1px' }}>{policy.description}</div>}
                  </div>
                  {policy.valueType === 'boolean' ? (
                    <button onClick={() => handlePolicyChange(policy.id, policy.value === 'true' ? 'false' : 'true')}
                      style={{
                        width: '44px', height: '24px', borderRadius: '12px', position: 'relative',
                        border: 'none', cursor: 'pointer', flexShrink: 0,
                        background: policy.value === 'true' ? 'var(--primary-600)' : 'var(--dark-700)',
                        transition: 'background 0.2s',
                      }}>
                      <span style={{
                        width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '3px', transition: 'left 0.2s',
                        left: policy.value === 'true' ? '23px' : '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                  ) : policy.valueType === 'number' ? (
                    <input
                      type="number" value={policy.value}
                      onChange={e => handlePolicyChange(policy.id, e.target.value)}
                      style={{
                        width: '80px', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '6px 10px', color: 'var(--input-text)', fontSize: '13px',
                        fontFamily: 'var(--font-body)', outline: 'none', textAlign: 'right',
                      }}
                    />
                  ) : policy.valueType === 'json' ? (
                    <span style={{ color: 'var(--dark-500)', fontSize: '11px', fontFamily: 'var(--font-code)' }}>
                      JSON Config
                    </span>
                  ) : (
                    <input
                      value={policy.value}
                      onChange={e => handlePolicyChange(policy.id, e.target.value)}
                      style={{
                        width: '140px', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '6px 10px', color: 'var(--input-text)', fontSize: '13px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </GlassCard>
        );
      })}
    </div>
  );

  // ─── Main Render ──────────────────────────────────────
  return (
    <>
      <TopBar title="Map Inventory" />
      <div style={{
        padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px',
        flex: 1, minHeight: 0,
      }}>
        {/* Toolbar Row 1: Tabs + Search + Stats + Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* View Tabs */}
          <div style={{
            display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden',
            border: '1px solid var(--glass-border)',
          }}>
            {viewTabs.map(tab => (
              <button key={tab.key} onClick={() => setViewMode(tab.key)}
                style={{
                  padding: '7px 14px', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: viewMode === tab.key ? 'var(--nav-item-active-bg)' : 'var(--bg-elevated)',
                  color: viewMode === tab.key ? 'var(--nav-item-active-color)' : 'var(--text-muted)',
                  fontFamily: 'var(--font-body)', transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}>
                <Icon icon={tab.icon} width={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search + Section filter (map/list only) */}
          {(viewMode === 'map' || viewMode === 'list') && (
            <>
              <div style={{ width: '200px' }}>
                <InputField placeholder="Search lots..." icon="solar:magnifer-bold-duotone"
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              </div>
              <select value={filterSection} onChange={e => setFilterSection(e.target.value)}
                style={{
                  background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)', padding: '8px 12px', color: 'var(--input-text)',
                  fontFamily: 'var(--font-body)', fontSize: '12px', outline: 'none', cursor: 'pointer',
                }}>
                <option value="">All Sections</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.name} — {s.label}</option>)}
              </select>
            </>
          )}

          <div style={{ flex: 1 }} />

          {/* Summary Stats (map/list only) */}
          {(viewMode === 'map' || viewMode === 'list') && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {[
                { label: 'Total', value: String(stats.total), icon: 'solar:layers-bold-duotone', color: 'var(--text-primary)' },
                { label: 'Value', value: fmt(stats.totalValue), icon: 'solar:wallet-bold-duotone', color: 'var(--primary-400)' },
                { label: 'Sold', value: fmt(stats.soldValue), icon: 'solar:tag-price-bold-duotone', color: '#22C55E' },
              ].map(s => (
                <div key={s.label} style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '5px 10px', borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-elevated)', border: '1px solid var(--border-base)',
                }}>
                  <Icon icon={s.icon} width={13} style={{ color: s.color, opacity: 0.7 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: s.color, fontFamily: 'var(--font-heading)' }}>{s.value}</span>
                    <span style={{ fontSize: '9px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick actions */}
          {viewMode === 'map' && (
            <div style={{ display: 'flex', gap: '6px' }}>
              {hasFreeformSections && (
                <>
                  <Button
                    variant={editMode ? 'primary' : 'secondary'} size="sm"
                    icon="solar:move-bold-duotone"
                    onClick={() => { if (editMode) { handleEditDone(); } else { setEditMode(true); setPlacementMode(false); } }}>
                    {editMode ? 'Done Editing' : 'Edit Layout'}
                  </Button>
                  <Button
                    variant={placementMode ? 'primary' : 'secondary'} size="sm"
                    icon="solar:map-point-add-bold-duotone"
                    onClick={() => { setPlacementMode(!placementMode); setEditMode(false); }}>
                    {placementMode ? 'Placing...' : 'Place Lot'}
                  </Button>
                </>
              )}
              <Button variant="secondary" size="sm" icon="solar:maximize-bold-duotone"
                onClick={() => { setFilterStatus(''); setFilterSection(''); setSelectedLot(null); setPlacementMode(false); setEditMode(false); }}>
                Reset View
              </Button>
            </div>
          )}
        </div>

        {/* Status filter pills (map/list) */}
        {(viewMode === 'map' || viewMode === 'list') && renderStatusBar()}

        {/* Content */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {viewMode === 'map' && renderMapView()}
          {viewMode === 'list' && renderListView()}
          {viewMode === 'designer' && renderDesigner()}
          {viewMode === 'policies' && renderPolicies()}
        </div>
      </div>

      {/* Section Editor Modal */}
      <Modal isOpen={showSectionModal} onClose={() => setShowSectionModal(false)}
        title={editingSection ? `Edit Section ${editingSection.name}` : 'New Section'} width="600px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowSectionModal(false)}>Cancel</Button>
            <Button variant="primary" icon="solar:diskette-bold-duotone" onClick={handleSaveSection}>
              {editingSection ? 'Update' : 'Create'} Section
            </Button>
          </>
        }>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <InputField label="Section Code" placeholder="e.g. G" value={sectionForm.name || ''}
            onChange={e => setSectionForm({ ...sectionForm, name: e.target.value })} />
          <InputField label="Display Name" placeholder="e.g. Garden of Hope" value={sectionForm.label || ''}
            onChange={e => setSectionForm({ ...sectionForm, label: e.target.value })} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Category</label>
            <select value={sectionForm.category || 'LAWN'} onChange={e => setSectionForm({ ...sectionForm, category: e.target.value })}
              style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none' }}>
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <InputField label="Phase" placeholder="e.g. Phase 3" value={sectionForm.phase || ''}
            onChange={e => setSectionForm({ ...sectionForm, phase: e.target.value })} />
        </div>
        <Divider spacing="12px" />
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Layout Mode</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          {(['GRID', 'FREEFORM', 'HYBRID'] as const).map(mode => (
            <button key={mode} onClick={() => setSectionForm({ ...sectionForm, layoutMode: mode })}
              style={{
                flex: 1, padding: '10px 8px', borderRadius: 'var(--radius-md)', border: 'none', cursor: 'pointer',
                background: (sectionForm.layoutMode || 'GRID') === mode ? 'var(--nav-item-active-bg)' : 'var(--bg-elevated)',
                color: (sectionForm.layoutMode || 'GRID') === mode ? 'var(--nav-item-active-color)' : 'var(--text-muted)',
                fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600,
                transition: 'all 0.15s ease', textAlign: 'center',
                outline: (sectionForm.layoutMode || 'GRID') === mode ? '1px solid rgba(13,148,136,0.40)' : 'none',
              }}>
              <Icon icon={mode === 'GRID' ? 'solar:grid-bold-duotone' : mode === 'FREEFORM' ? 'solar:map-point-bold-duotone' : 'solar:layers-bold-duotone'}
                width={16} style={{ display: 'block', margin: '0 auto 4px' }} />
              {mode}
              <div style={{ fontSize: '9px', color: 'var(--dark-500)', marginTop: '2px' }}>
                {mode === 'GRID' ? 'Rows × Cols' : mode === 'FREEFORM' ? 'Single plots' : 'Grid + extras'}
              </div>
            </button>
          ))}
        </div>
        {(sectionForm.layoutMode || 'GRID') !== 'FREEFORM' && (
          <>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Grid Configuration</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
              <InputField label="Rows" type="number" value={String(sectionForm.rows || 4)}
                onChange={e => setSectionForm({ ...sectionForm, rows: parseInt(e.target.value) || 4 })} />
              <InputField label="Columns" type="number" value={String(sectionForm.cols || 6)}
                onChange={e => setSectionForm({ ...sectionForm, cols: parseInt(e.target.value) || 6 })} />
              <InputField label="Lot Gap (px)" type="number" value={String(sectionForm.lotGap || 6)}
                onChange={e => setSectionForm({ ...sectionForm, lotGap: parseInt(e.target.value) || 6 })} />
            </div>
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginTop: '14px' }}>
          <InputField label="Lot Width" type="number" value={String(sectionForm.lotWidth || 48)}
            onChange={e => setSectionForm({ ...sectionForm, lotWidth: parseInt(e.target.value) || 48 })} />
          <InputField label="Lot Height" type="number" value={String(sectionForm.lotHeight || 48)}
            onChange={e => setSectionForm({ ...sectionForm, lotHeight: parseInt(e.target.value) || 48 })} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Color</label>
            <input type="color" value={sectionForm.color || '#22C55E'}
              onChange={e => setSectionForm({ ...sectionForm, color: e.target.value })}
              style={{ width: '100%', height: '34px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent' }} />
          </div>
        </div>
        <Divider spacing="12px" />
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Map Position</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
          <InputField label="X Position" type="number" value={String(sectionForm.x || 0)}
            onChange={e => setSectionForm({ ...sectionForm, x: parseInt(e.target.value) || 0 })} />
          <InputField label="Y Position" type="number" value={String(sectionForm.y || 0)}
            onChange={e => setSectionForm({ ...sectionForm, y: parseInt(e.target.value) || 0 })} />
          <InputField label="Width" type="number" value={String(sectionForm.width || 400)}
            onChange={e => setSectionForm({ ...sectionForm, width: parseInt(e.target.value) || 400 })} />
          <InputField label="Height" type="number" value={String(sectionForm.height || 300)}
            onChange={e => setSectionForm({ ...sectionForm, height: parseInt(e.target.value) || 300 })} />
        </div>

        {/* Live preview */}
        <Divider spacing="12px" />
        {(sectionForm.layoutMode || 'GRID') === 'FREEFORM' ? (
          <>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
              Freeform Section
            </div>
            <div style={{
              height: '100px', borderRadius: '8px', background: 'var(--bg-elevated)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '6px',
              border: '1px dashed rgba(148,163,184,0.15)',
            }}>
              <Icon icon="solar:map-point-bold-duotone" width={24} style={{ color: 'var(--dark-500)' }} />
              <span style={{ color: 'var(--dark-500)', fontSize: '11px' }}>Individual plots will be placed manually after creation</span>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
              Preview ({(sectionForm.rows || 4) * (sectionForm.cols || 6)} lots)
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${sectionForm.cols || 6}, 1fr)`,
              gap: `${Math.max(1, (sectionForm.lotGap || 6) / 2)}px`,
              padding: '12px', borderRadius: '8px', background: 'rgba(15,23,42,0.50)',
            }}>
              {Array.from({ length: (sectionForm.rows || 4) * (sectionForm.cols || 6) }).map((_, i) => (
                <div key={i} style={{
                  aspectRatio: `${sectionForm.lotWidth || 48} / ${sectionForm.lotHeight || 48}`,
                  borderRadius: '2px', background: `${sectionForm.color || '#22C55E'}60`,
                  minHeight: '12px',
                }} />
              ))}
            </div>
            {(sectionForm.layoutMode || 'GRID') === 'HYBRID' && (
              <div style={{ fontSize: '10px', color: 'var(--dark-500)', marginTop: '6px', textAlign: 'center' }}>
                + Additional freeform plots can be added after creation
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Lot Type Editor Modal */}
      <Modal isOpen={showLotTypeModal} onClose={() => setShowLotTypeModal(false)}
        title={editingLotType ? `Edit: ${editingLotType.name}` : 'New Lot Type'} width="560px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowLotTypeModal(false)}>Cancel</Button>
            <Button variant="primary" icon="solar:diskette-bold-duotone" onClick={handleSaveLotType}>
              {editingLotType ? 'Update' : 'Create'} Lot Type
            </Button>
          </>
        }>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <InputField label="Name" placeholder="e.g. Premium Lawn" value={lotTypeForm.name || ''}
            onChange={e => setLotTypeForm({ ...lotTypeForm, name: e.target.value })} />
          <InputField label="Code" placeholder="e.g. PL" value={lotTypeForm.code || ''}
            onChange={e => setLotTypeForm({ ...lotTypeForm, code: e.target.value.toUpperCase() })} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Category</label>
            <select value={lotTypeForm.category || 'LAWN'} onChange={e => setLotTypeForm({ ...lotTypeForm, category: e.target.value })}
              style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none' }}>
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <InputField label="Base Price (₱)" type="number" value={String(lotTypeForm.basePrice || 0)}
            onChange={e => setLotTypeForm({ ...lotTypeForm, basePrice: parseFloat(e.target.value) || 0 })} />
        </div>
        <div style={{ marginTop: '14px' }}>
          <InputField label="Description" placeholder="Brief description of this lot type" value={lotTypeForm.description || ''}
            onChange={e => setLotTypeForm({ ...lotTypeForm, description: e.target.value })} />
        </div>
        <Divider spacing="12px" />
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Dimensions & Capacity</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
          <InputField label="Width (px)" type="number" value={String(lotTypeForm.defaultWidth || 48)}
            onChange={e => setLotTypeForm({ ...lotTypeForm, defaultWidth: parseInt(e.target.value) || 48 })} />
          <InputField label="Height (px)" type="number" value={String(lotTypeForm.defaultHeight || 48)}
            onChange={e => setLotTypeForm({ ...lotTypeForm, defaultHeight: parseInt(e.target.value) || 48 })} />
          <InputField label="Capacity" type="number" value={String(lotTypeForm.capacity || 1)}
            onChange={e => setLotTypeForm({ ...lotTypeForm, capacity: parseInt(e.target.value) || 1 })} />
        </div>
        <Divider spacing="12px" />
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Photo</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {lotTypeForm.imageUrl && (
            <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
              <img src={lotTypeForm.imageUrl} alt="Lot type preview"
                style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} />
              <button onClick={() => setLotTypeForm({ ...lotTypeForm, imageUrl: '' })}
                style={{
                  position: 'absolute', top: '8px', right: '8px',
                  background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(8px)',
                  border: 'none', borderRadius: '6px', width: '28px', height: '28px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#fff',
                }}>
                <Icon icon="solar:trash-bin-trash-bold-duotone" width={14} />
              </button>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '10px 14px', borderRadius: 'var(--radius-md)',
                background: 'rgba(13,148,136,0.10)', border: '1px solid rgba(13,148,136,0.30)',
                cursor: 'pointer', fontFamily: 'var(--font-body)', color: 'var(--primary-400)',
                fontSize: '13px', fontWeight: 600, transition: 'all 0.15s',
              }}>
                <Icon icon="solar:upload-bold-duotone" width={16} />
                Upload Photo
                <input type="file" accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => setLotTypeForm({ ...lotTypeForm, imageUrl: reader.result as string });
                      reader.readAsDataURL(file);
                    }
                  }}
                  style={{ display: 'none' }} />
              </label>
            </div>
            <div style={{ flex: 1 }}>
              <InputField placeholder="Or paste image URL" value={lotTypeForm.imageUrl || ''}
                onChange={e => setLotTypeForm({ ...lotTypeForm, imageUrl: e.target.value })} />
            </div>
          </div>
        </div>
        <Divider spacing="12px" />
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Appearance</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <IconPicker value={lotTypeForm.icon || ''}
            onChange={icon => setLotTypeForm({ ...lotTypeForm, icon })}
            color={lotTypeForm.color || '#0D9488'} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Color</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="color" value={lotTypeForm.color || '#0D9488'}
                onChange={e => setLotTypeForm({ ...lotTypeForm, color: e.target.value })}
                style={{ width: '40px', height: '34px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontFamily: 'var(--font-code)' }}>{lotTypeForm.color || '#0D9488'}</span>
            </div>
          </div>
        </div>
        {/* Preview */}
        <Divider spacing="12px" />
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Preview</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px', borderRadius: '8px', background: 'var(--bg-elevated)' }}>
          <div style={{
            width: `${Math.min(80, lotTypeForm.defaultWidth || 48)}px`,
            height: `${Math.min(80, lotTypeForm.defaultHeight || 48)}px`,
            borderRadius: '4px', background: `${lotTypeForm.color || '#0D9488'}90`,
            border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>{lotTypeForm.code || '??'}</span>
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>{lotTypeForm.name || 'Untitled'}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
              {CATEGORY_CONFIG[lotTypeForm.category]?.label || lotTypeForm.category} · {lotTypeForm.defaultWidth || 48}×{lotTypeForm.defaultHeight || 48}px · Cap: {lotTypeForm.capacity || 1}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, marginTop: '2px' }}>{fmt(lotTypeForm.basePrice || 0)}</div>
          </div>
        </div>
      </Modal>

      {/* Freeform Lot Creation Modal */}
      <Modal isOpen={showFreeformModal} onClose={() => setShowFreeformModal(false)}
        title={`Add Single Plot — ${freeformSection?.label || ''}`} width="500px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowFreeformModal(false)}>Cancel</Button>
            <Button variant="primary" icon="solar:add-circle-bold-duotone" onClick={handleSaveFreeformLot}>
              Create Plot
            </Button>
          </>
        }>
        <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '6px', background: 'rgba(13,148,136,0.08)', borderLeft: '3px solid var(--primary-600)' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
            Place an individual lot at a specific position within this section. Use this for irregular land areas that don't fit a uniform grid.
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <InputField label="Lot Number" placeholder="e.g. E10" value={freeformForm.lotNumber || ''}
            onChange={e => setFreeformForm({ ...freeformForm, lotNumber: e.target.value })} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lot Type</label>
            <select value={freeformForm.lotTypeId || ''} onChange={e => {
              const lt = lotTypes.find(t => t.id === e.target.value);
              setFreeformForm({ ...freeformForm, lotTypeId: e.target.value, price: lt?.basePrice || freeformForm.price, width: lt?.defaultWidth || freeformForm.width, height: lt?.defaultHeight || freeformForm.height });
            }}
              style={{ background: 'rgba(15,23,42,0.50)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', color: 'var(--dark-200)', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none' }}>
              <option value="">No type</option>
              {lotTypes.filter(t => t.isActive).map(lt => <option key={lt.id} value={lt.id}>{lt.name} ({lt.code})</option>)}
            </select>
          </div>
          <InputField label="Price (₱)" type="number" value={String(freeformForm.price || 0)}
            onChange={e => setFreeformForm({ ...freeformForm, price: parseFloat(e.target.value) || 0 })} />
          <div />
        </div>
        <Divider spacing="12px" />
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
          Position & Dimensions (relative to section)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
          <InputField label="X Offset" type="number" value={String(freeformForm.x_coord || 0)}
            onChange={e => setFreeformForm({ ...freeformForm, x_coord: parseInt(e.target.value) || 0 })} />
          <InputField label="Y Offset" type="number" value={String(freeformForm.y_coord || 0)}
            onChange={e => setFreeformForm({ ...freeformForm, y_coord: parseInt(e.target.value) || 0 })} />
          <InputField label="Width" type="number" value={String(freeformForm.width || 60)}
            onChange={e => setFreeformForm({ ...freeformForm, width: parseInt(e.target.value) || 60 })} />
          <InputField label="Height" type="number" value={String(freeformForm.height || 60)}
            onChange={e => setFreeformForm({ ...freeformForm, height: parseInt(e.target.value) || 60 })} />
        </div>
        {/* Mini position preview */}
        <div style={{ marginTop: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Preview</div>
          <div style={{
            position: 'relative', height: '120px', borderRadius: '8px',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-base)',
            overflow: 'hidden',
          }}>
            {freeformSection && (() => {
              const sw = freeformSection.width || 400;
              const sh = freeformSection.height || 300;
              const relX = (Number(freeformForm.x_coord || 0) / sw) * 100;
              const relY = (Number(freeformForm.y_coord || 0) / sh) * 100;
              const relW = (Number(freeformForm.width || 60) / sw) * 100;
              const relH = (Number(freeformForm.height || 60) / sh) * 100;
              return (
                <>
                  {/* Existing lots */}
                  {lots.filter(l => l.sectionId === freeformSection.id).map(lot => {
                    const lx = ((lot.x_coord - freeformSection.x) / sw) * 100;
                    const ly = ((lot.y_coord - freeformSection.y) / sh) * 100;
                    const lw = ((lot.width || freeformSection.lotWidth || 50) / sw) * 100;
                    const lh = ((lot.height || freeformSection.lotHeight || 50) / sh) * 100;
                    return (
                      <div key={lot.id} style={{
                        position: 'absolute', left: `${lx}%`, top: `${ly}%`,
                        width: `${Math.max(lw, 3)}%`, height: `${Math.max(lh, 5)}%`,
                        borderRadius: '2px', background: STATUS_CONFIG[lot.status]?.color || '#64748B', opacity: 0.4,
                      }} />
                    );
                  })}
                  {/* New lot preview */}
                  <div style={{
                    position: 'absolute', left: `${relX}%`, top: `${relY}%`,
                    width: `${Math.max(relW, 3)}%`, height: `${Math.max(relH, 5)}%`,
                    borderRadius: '3px', background: 'rgba(13,148,136,0.60)',
                    border: '2px solid var(--primary-400)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ color: '#fff', fontSize: '8px', fontWeight: 700 }}>{freeformForm.lotNumber || '?'}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </Modal>

      {/* Detail modal for list view */}
      {viewMode === 'list' && selectedLot && (
        <Modal isOpen={!!selectedLot} onClose={() => setSelectedLot(null)}
          title={`Lot ${selectedLot.lotNumber}`} width="500px"
          footer={
            <>
              <Button variant="secondary" onClick={() => setSelectedLot(null)}>Close</Button>
              {selectedLot.status === 'AVAILABLE' && (
                <>
                  <Button variant="secondary" icon="solar:clock-circle-bold-duotone"
                    style={{ borderColor: 'rgba(245,158,11,0.40)', color: '#F59E0B' }}
                    onClick={() => { handleStatusChange(selectedLot.id, 'RESERVED'); setSelectedLot(null); }}>
                    Reserve
                  </Button>
                  <Button variant="primary" icon="solar:sale-bold-duotone"
                    onClick={() => { handleStatusChange(selectedLot.id, 'SOLD'); setSelectedLot(null); }}>
                    Sell
                  </Button>
                </>
              )}
            </>
          }>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              ['Section', sections.find(s => s.id === selectedLot.sectionId)?.label || selectedLot.sectionId],
              ['Type', selectedLot.lotType?.name || '—'],
              ['Price', fmt(selectedLot.price)],
              ['Status', selectedLot.status],
              ['Position', selectedLot.isFreeform ? `Freeform (${Math.round(selectedLot.x_coord)}, ${Math.round(selectedLot.y_coord)})` : `Row ${selectedLot.row + 1}, Col ${selectedLot.col + 1}`],
              ['Capacity', String(selectedLot.lotType?.capacity || 1)],
            ].map(([label, value], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--dark-500)', fontSize: '13px' }}>{label}</span>
                <span style={{ color: 'var(--dark-200)', fontSize: '13px', fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
};

export default Inventory;